/**
 * @fileOverview
 * Demo Application
 */
import React from 'react';
import { HashRouter as ReactRouter } from 'react-router-dom';
import { ApolloProvider } from '@apollo/client/react';
import { setupFDS } from '@tcc/fds';
import * as SharedDataLib from '@tcc/shared-data-lib';
import { DD } from '@tcc/analytics';
import { AppRouter } from './router/AppRouter/AppRouter';
import { getGraphQlClient } from './graphql/graphqlClient';
import './utils/datadog';
import { renderUsingContextVersion } from './utils/renderUsingContextVersion';

type SDLQueryProviderComponent = React.ComponentType<React.PropsWithChildren<unknown>>;

// Prefer SDLQueryProvider when available, but allow older shared-data-lib builds.
const SDLQueryProvider = (
  SharedDataLib as unknown as { SDLQueryProvider?: SDLQueryProviderComponent }
).SDLQueryProvider ?? React.Fragment;

declare global {
  interface Window {
    timerStart: number;
    timeEvents: {
      fieldView: {
        appStart: number;
      };
    };
  }
}

window.timerStart = Date.now();

// Report how long load-lifter took to load all @tcc runtime libraries: from just
// before load-lifter runs (window.timeEvents.fieldView.appStart, set in index.html)
// until the app bundle's imports have resolved (window.timerStart, set above).
// Both are read-only here, so existing time measurements are unaffected.
const librariesLoadStart = window.timeEvents?.fieldView?.appStart;
if (librariesLoadStart) {
  DD.addDurationVital('tcc_libraries_load', {
    startTime: librariesLoadStart,
    duration: window.timerStart - librariesLoadStart,
    context: { source: 'load-lifter' },
    description: 'Time for load-lifter to load all @tcc runtime libraries',
  });
}

setupFDS();
const graphQlClient = getGraphQlClient();

const App = () =>
  (
    <ApolloProvider client={graphQlClient}>
      <SDLQueryProvider>
        <ReactRouter>
          <AppRouter />
        </ReactRouter>
      </SDLQueryProvider>
    </ApolloProvider>
  );

renderUsingContextVersion(<App />, document.getElementById('root'));
